import { useMutation, useQuery } from "@apollo/client/react";
import { CombinedGraphQLErrors, ServerError } from "@apollo/client/errors";
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import React, { Fragment, useContext, useEffect, useState } from "react";
import authService from "../../services/authService";
import AuthLayout from "../common/AuthLayout/AuthLayout";
import styles from "./NotificationEmailPage.module.css";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Formik } from "formik";
import { filter, find } from "lodash";
import * as yup from "yup";
import { NotificationEmailFormValues } from "./EmailValidation";
import AppContext from "../../context/AppContext";
import { UsersEmailConfigVariables, UsersEmailConfig } from "./types/UsersEmailConfig";
import { GetNotificationTypes } from "./types/GetNotificationTypes";
import getNotificationTypesQuery from "./GetNotificationTypesQuery";
import getUsersEmailConfig from "./NotificationSettingsQuery";
import getUserEmailMobile from "./UserEmailMobileQuery";
import UsersEmailConfigMutation from "./NotificationSettingsMutation";
import { GetUsersEmailConfig } from "./types/GetUsersEmailConfig";
import { userConfig, userEmail, userMobile, smsDelivery } from "../../graphql/types/gql-global-types";
import Loading from "../Loading/Loading";
import { GetUserEmailMobile, GetUserEmailMobile_getUserDevice_data } from "./types/GetUserEmailMobile";
import MobileNumberField from "../common/InputField/MobileNumberField";
import SettingsIcon from "@mui/icons-material/Settings";
import { isValidMobileNumber, MOBILE_NUMBER_FORMAT_MESSAGE } from "../../utils/mobile";
import StandardizedHeader from "../GeneralHeader/StandardizedHeader";

type NotificationEmailBoxType = {
  onSubmit: (
    notificationConfig: NotificationType[],
    notificationEmail: userEmail[],
    notificationMobile: userMobile[],
    smsDelivery: smsDelivery,
  ) => Promise<void>;
  error?: any;
  loading?: boolean;
  submitSuccess: boolean;
  errorMessage?: string;
};

type NotificationTimeFrames = {
  type: string;
  minute: number;
  checked: boolean;
};

type NotificationType = {
  id: number;
  type: string;
  checked: boolean;
  timeframe: NotificationTimeFrames[];
  pendingEmail: boolean;
};

export const NotificationEmailBox: React.FC<NotificationEmailBoxType> = (props) => {
  const { error, loading, submitSuccess } = props;

  const initialValues: NotificationEmailFormValues = {
    message: "",
    newEmail: "",
    mobile: "",
  };

  const isDesktop = useContext(AppContext).isDesktop;
  const mobility = authService.isMobility();
  const [emailNotifications, setEmailNotifications] = useState<userEmail[]>([]);
  const [mobileNotifications, setMobileNotifications] = useState<userMobile[]>([]);
  const [devices, setDevices] = useState<GetUserEmailMobile_getUserDevice_data[]>([]);
  const [hideme, setHideme] = useState("none");
  const [confirmNotification, setConfirmNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [deliveryId, setDeliveryId] = useState(0);
  const [onlyBusinessHours, setOnlyBusinessHours] = useState(false);
  const [includeWeekends, setIncludeWeekends] = useState(false);

  const notificationTimeFrames: NotificationTimeFrames[] = [
    { type: "10 minutes", minute: 10, checked: false },
    { type: "hourly", minute: 60, checked: false },
    { type: "daily", minute: 1440, checked: false },
    { type: "none", minute: 0, checked: true },
  ];
  //        { type: 'none', checked: false },
  const notificationTypes: NotificationType[] = [];

  const [notificationConfig, setNotificationConfig] = useState<NotificationType[]>(notificationTypes);

  const { data: nconfig, loading: configLoading } = useQuery<GetNotificationTypes>(getNotificationTypesQuery);

  const { data: dataNConfigs, loading: emailConfigLoading } = useQuery<GetUsersEmailConfig>(getUsersEmailConfig, {
    variables: { userid: parseInt(authService.getUserId()) },
    fetchPolicy: "network-only",
  });

  const { data: dataUsersEmailMobile, loading: usersEmailMobileLoading } = useQuery<GetUserEmailMobile>(
    getUserEmailMobile,
    {
      variables: { userid: parseInt(authService.getUserId()) },
      fetchPolicy: "network-only",
    },
  );

  useEffect(() => {
    if (nconfig) {
      if (nconfig.getNotificationTypes && nconfig.getNotificationTypes.data) {
        let summaryChecked = false;
        const newNotificationTypes: NotificationType[] = [];
        nconfig.getNotificationTypes.data.map((notType: any) => {
          const notifyTimeFrame = JSON.parse(JSON.stringify(notificationTimeFrames)); //Old ways to deep clone without changing const.
          let mainChecked = false;
          let pendingChecked = false;
          if (dataNConfigs) {
            const usersEmailConfigList = dataNConfigs.getUsersEmailConfig.config;
            const fNotifyConfig = usersEmailConfigList.find((f: any) => f.notificationTypeId === notType.id);
            if (fNotifyConfig) {
              const timeFrameId = notifyTimeFrame.findIndex((obj: any) => obj.minute === fNotifyConfig.duration);
              pendingChecked = fNotifyConfig.pendingEmail === "true";
              if (notType.id === 1) {
                summaryChecked = true;
                notifyTimeFrame[timeFrameId].checked = true;
                notifyTimeFrame[notifyTimeFrame.length - 1].checked = false; //reset the none to false
                mainChecked = fNotifyConfig.notificationTypeId === notType.id;
              } else {
                if (!summaryChecked) {
                  notifyTimeFrame[timeFrameId].checked = true;
                  notifyTimeFrame[notifyTimeFrame.length - 1].checked = false; //reset the none to false
                  mainChecked = fNotifyConfig.notificationTypeId === notType.id;
                }
              }
            }
          }
          newNotificationTypes.push({
            id: notType.id,
            type: notType.typename,
            checked: mainChecked,
            timeframe: notifyTimeFrame,
            pendingEmail: pendingChecked,
          });

          return newNotificationTypes;
        });
        setNotificationConfig(newNotificationTypes);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nconfig, dataNConfigs]);

  useEffect(() => {
    if (dataUsersEmailMobile) {
      const usersEmailList = dataUsersEmailMobile.getUserEmail.data;
      setEmailNotifications(
        usersEmailList.map((u) => ({
          id: u.id || 0,
          email: u.email || "",
          enabled: u.enabled || false,
        })),
      );

      const usersMobileList = dataUsersEmailMobile.getUserMobile.data;
      setMobileNotifications(
        usersMobileList.map((u) => ({
          id: u.id || 0,
          mobile: u.mobile || "",
          enabled: u.enabled || false,
        })),
      );

      const smsDelivery = dataUsersEmailMobile.getSmsDelivery.data;
      setDeliveryId((smsDelivery.id as number) || 0);
      setOnlyBusinessHours((smsDelivery.onlyBusinessHours as boolean) || false);
      setIncludeWeekends((smsDelivery.includeWeekends as boolean) || false);

      setDevices(dataUsersEmailMobile.getUserDevice.data);

      setHideme("block");
    } else {
      setHideme("none");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUsersEmailMobile]);

  const emailSchema = yup.string().email("Enter valid email(s)").required("Email(s) are required");
  const mobileSchema = yup
    .string()
    .required("Mobile Number(s) are required")
    // Shared canonical mobile validation (see utils/mobile.ts) — required here, so the
    // value must be a valid AU mobile (10 digits, "04" prefix). Stored digits-only.
    .test("Mobile Test", MOBILE_NUMBER_FORMAT_MESSAGE, isValidMobileNumber);

  const submit = () => {
    // Validate all email notifications using Yup
    for (const emailNotification of emailNotifications) {
      try {
        emailSchema.validateSync(emailNotification.email);
      } catch (err: any) {
        setErrorMessage(err.message);
        return;
      }
    }

    // KI-002: reject duplicate notification emails instead of silently deduplicating on save.
    // Compare case-insensitively on the trimmed address so "A@x.com" and "a@x.com " are treated
    // as the same. Surfacing this to the user (rather than the server quietly collapsing dupes)
    // makes the outcome explicit.
    const seenEmails = new Set<string>();
    for (const emailNotification of emailNotifications) {
      const normalised = (emailNotification.email || "").trim().toLowerCase();
      if (!normalised) continue;
      if (seenEmails.has(normalised)) {
        setErrorMessage("Duplicate email address. Each notification email must be unique.");
        return;
      }
      seenEmails.add(normalised);
    }

    // Validate all mobile notifications using Yup
    for (const mobileNotification of mobileNotifications) {
      try {
        mobileSchema.validateSync(mobileNotification.mobile);
      } catch (err: any) {
        setErrorMessage(err.message);
        return;
      }
    }

    const checkConfig = filter(notificationConfig, (config) => {
      const isNotiTimeframeFalse = find(config.timeframe, ["checked", true]);
      return config.checked === true && isNotiTimeframeFalse !== undefined;
    });

    const isNotiTimeframeFalse1 = find(checkConfig, {
      timeframe: ["checked", false],
    });

    if (isNotiTimeframeFalse1 !== undefined) {
      setErrorMessage("Please select at least one notification config");
      return;
    }

    if (!confirmNotification) {
      setErrorMessage("Please confirm notification settings");
      return;
    }

    const smsDelivery: smsDelivery = {
      id: deliveryId,
      onlyBusinessHours: onlyBusinessHours,
      includeWeekends: onlyBusinessHours ? includeWeekends : false,
    };

    setErrorMessage("");
    props.onSubmit(notificationConfig, emailNotifications, mobileNotifications, smsDelivery);
  };

  const handleEmailNotificationText = (i: any) => (e: any) => {
    const emailnotify = [...emailNotifications];
    const updatedEmailNotify = emailnotify.map((x, idx) => (idx === i ? { ...x, email: e.target.value.trim() } : x));
    setEmailNotifications(updatedEmailNotify);
  };

  const handleEmailNotificationEnabled = (i: any) => (e: any) => {
    const emailnotify = [...emailNotifications];
    const updatedEmailNotify = emailnotify.map((x, idx) => (idx === i ? { ...x, enabled: e.target.checked } : x));
    setEmailNotifications(updatedEmailNotify);
  };

  const handleDeleteEmailNotification = (i: any) => (e: any) => {
    e.preventDefault();
    const emailnotify = [...emailNotifications.slice(0, i), ...emailNotifications.slice(i + 1)];
    setEmailNotifications(emailnotify);
  };

  const handleAddEmailNotification = (e: any) => {
    e.preventDefault();
    if (hideme === "block") {
      const emailnotify = emailNotifications.concat([{ id: 0, email: "", enabled: true }]);
      setEmailNotifications(emailnotify);
    }

    setHideme("block");
  };

  const handleMobileNotificationText = (i: any, value: any) => {
    const mobileNotify = [...mobileNotifications];

    const updatedMobileNotify = mobileNotify.map((x, idx) => (idx === i ? { ...x, mobile: value } : x));
    setMobileNotifications(updatedMobileNotify);
  };

  const handleMobileNotificationEnabled = (i: any) => (e: any) => {
    const mobileNotify = [...mobileNotifications];
    const updatedMobileNotify = mobileNotify.map((x, idx) => (idx === i ? { ...x, enabled: e.target.checked } : x));
    setMobileNotifications(updatedMobileNotify);
  };

  const handleDeleteMobileNotification = (i: any) => (e: any) => {
    e.preventDefault();
    const mobileNotify = [...mobileNotifications.slice(0, i), ...mobileNotifications.slice(i + 1)];
    setMobileNotifications(mobileNotify);
  };

  const handleAddMobileNotification = (e: any) => {
    e.preventDefault();
    if (hideme === "block") {
      const mobileNotify = mobileNotifications.concat([{ id: 0, mobile: "", enabled: true }]);
      setMobileNotifications(mobileNotify);
    }

    setHideme("block");
  };

  const handlePendingEmailCheck = (e: any, c: any) => {
    setNotificationConfig(
      notificationConfig.map((ntype) => {
        const updatedConfig = { ...ntype };
        const ty = ntype.id;
        if (parseInt(e.target.name.split("-")[1]) === ty) {
          updatedConfig.pendingEmail = c;
        }

        return updatedConfig;
      }),
    );
  };
  const handleAddNotificationConfig = (e: any) => {
    setNotificationConfig(
      notificationConfig.map((ntype) => {
        const updatedConfig = { ...ntype };

        const ty = ntype.id;
        if (ty === 1) {
          //Only for summary to set it to none

          updatedConfig.timeframe = ntype.timeframe.map((time) => {
            const times = { ...time };
            if (`${ty}-${time.type}` === `${ty}-none`) times.checked = e.target.checked;
            else times.checked = false;

            return times;
          });
        }
        if (parseInt(e.target.value.split("-")[0]) === ty) {
          updatedConfig.timeframe = ntype.timeframe.map((time) => {
            const times = { ...time };
            if (`${ty}-${time.type}` === e.target.value) times.checked = e.target.checked;
            else times.checked = false;

            return times;
          });
        }

        return updatedConfig;
      }),
    );
  };

  const handleSummaryNotification = (e: any) => {
    setNotificationConfig(
      notificationConfig.map((ntype) => {
        const updatedConfig = { ...ntype };
        const ty = ntype.id;
        if (parseInt(e.target.value.split("-")[0]) === ty) {
          updatedConfig.timeframe = ntype.timeframe.map((time) => {
            const times = { ...time };
            if (`${ty}-${time.type}` === e.target.value) times.checked = e.target.checked;
            else times.checked = false;
            return times;
          });
        } else {
          updatedConfig.pendingEmail = false;
          updatedConfig.timeframe = ntype.timeframe.map((time) => {
            const times = { ...time };
            if (`${ty}-${time.type}` === `${ty}-none`) times.checked = e.target.checked;
            else times.checked = false;
            return times;
          });
        }

        return updatedConfig;
      }),
    );
  };

  return (
    <Grid container className={isDesktop ? styles.mainBox : styles.mainBoxM}>
      <Grid size={{ xs: 12 }}>
        <AuthLayout title="" hideHeading maxWidth={"900px"}>
          <Box mt={5}>
            <Formik initialValues={initialValues} onSubmit={submit}>
              {({ errors, handleSubmit, touched }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <span>
                      <Box>
                        {error && (() => {
                          if (CombinedGraphQLErrors.is(error)) {
                            return error.errors.map((graphqlError, index) => (
                              <Typography key={index} color="error">
                                {graphqlError.message}
                              </Typography>
                            ));
                          } else if (ServerError.is(error)) {
                            return <Typography color="error">{error.message}</Typography>;
                          } else {
                            return <Typography color="error">{(error as any)?.message || 'An error occurred'}</Typography>;
                          }
                        })()}
                      </Box>
                      <Box>
                        {errors.message && touched.newEmail && <Typography color="error">{errors.message}</Typography>}
                      </Box>
                      <Box mt={2} className={styles.notifyEmailHeader}>
                        <Typography>Notification Types</Typography>
                      </Box>
                      <Box className={styles.notifyBox}>
                        {configLoading || emailConfigLoading ? (
                          <Loading />
                        ) : (
                          notificationConfig.map((ntype) => {
                            return (
                              <Fragment key={ntype.id}>
                                <Grid container spacing={1} alignItems="center" justifyContent="center" data-testid={`notif-${ntype.type?.toLowerCase().replace(/\s+/g, '-')}`}>
                                  <Grid
                                    size={{
                                      xs: 12,
                                      md: 4,
                                    }}
                                  >
                                    <Typography>{ntype.type}</Typography>
                                  </Grid>
                                  <Grid
                                    size={{
                                      xs: 12,
                                      md: 8,
                                    }}
                                  >
                                    <RadioGroup
                                      row
                                      aria-labelledby="demo-radio-buttons-group-label"
                                      name="radio-buttons-group"
                                    >
                                      {ntype.timeframe.map((timef, index) => {
                                        return (
                                          <FormControlLabel
                                            value={`${ntype.id}-${timef.type}`}
                                            key={index}
                                            control={
                                              <Radio
                                                checked={timef.checked}
                                                color="primary"
                                                onChange={
                                                  ntype.id === 1
                                                    ? handleSummaryNotification
                                                    : handleAddNotificationConfig
                                                }
                                              />
                                            }
                                            label={timef.type}
                                          />
                                        );
                                      })}
                                      <Box>
                                        <Checkbox
                                          name={`pending-${ntype.id}`}
                                          color="primary"
                                          checked={ntype.pendingEmail}
                                          onChange={(e, checked) => {
                                            handlePendingEmailCheck(e, checked);
                                          }}
                                        />
                                        inc. pending
                                      </Box>
                                    </RadioGroup>
                                  </Grid>
                                </Grid>
                                {(!isDesktop || mobility) && <hr />}
                              </Fragment>
                            );
                          })
                        )}
                      </Box>
                      <Box style={{ marginTop: "20px" }}>
                        <hr />
                      </Box>
                      <Box>
                        <Typography color="primary" className={styles.subHeading}>
                          Email Notifications
                        </Typography>
                      </Box>
                      <Box>
                        <Typography className={styles.paddingTop} color="grey.100">
                          Your email will be used for new results notifications.
                        </Typography>
                      </Box>
                      <Box className={styles.notifyEmailHeader}>
                        <Typography>Add or remove emails</Typography>
                      </Box>
                      <Box className={styles.notifyEmailBox}>
                        {usersEmailMobileLoading ? (
                          <Loading />
                        ) : (
                          <>
                            <Button
                              variant="contained"
                              size="small"
                              className={styles.button}
                              startIcon={<AddCircleOutlineIcon />}
                              onClick={handleAddEmailNotification}
                            >
                              Add Email
                            </Button>
                            <br />
                            <Box
                              id="emailN"
                              //display={hideme}
                              className={styles.paddingTop}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                }}
                                key={99999}
                              >
                                <Box sx={{ flexGrow: 8, flexBasis: "10%" }}></Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexGrow: 1,
                                    alignContent: "center",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Typography>Remove</Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexGrow: 1,
                                    alignContent: "center",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Typography>Active</Typography>
                                </Box>
                              </Box>
                              {emailNotifications.map((emailNotification, index) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                  }}
                                  key={index}
                                  data-testid="notif-email-input"
                                >
                                  <Box sx={{ flexGrow: 8, flexBasis: "10%" }}>
                                    <TextField
                                      key={"email-" + index}
                                      label="Email"
                                      placeholder="Input Email"
                                      variant="outlined"
                                      className={styles.specialInput}
                                      onChange={handleEmailNotificationText(index)}
                                      size="small"
                                      value={emailNotification.email}
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexGrow: 1,
                                      alignContent: "center",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <IconButton
                                      color="secondary"
                                      aria-label="Delete Email"
                                      component="span"
                                      size="small"
                                      onClick={handleDeleteEmailNotification(index)}
                                    >
                                      <HighlightOffIcon />
                                    </IconButton>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexGrow: 1,
                                      alignContent: "center",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Checkbox
                                      key={"enabled-" + index}
                                      color="primary"
                                      checked={emailNotification.enabled ? emailNotification.enabled : false}
                                      onChange={handleEmailNotificationEnabled(index)}
                                    />
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </>
                        )}
                      </Box>
                      <Box style={{ marginTop: "20px" }}>
                        <hr />
                      </Box>
                      <Box>
                        <Typography color="primary" className={styles.subHeading}>
                          SMS Notifications
                        </Typography>
                      </Box>
                      <Box>
                        <Typography className={styles.paddingTop} color="grey.100">
                          Your mobile number will be used for new results notifications.
                        </Typography>
                      </Box>
                      <Box className={styles.notifyEmailHeader}>
                        <Typography>Add or remove mobile numbers</Typography>
                      </Box>
                      <Box className={styles.notifyEmailBox}>
                        {usersEmailMobileLoading ? (
                          <Loading />
                        ) : (
                          <>
                            <Button
                              variant="contained"
                              size="small"
                              className={styles.button}
                              startIcon={<AddCircleOutlineIcon />}
                              onClick={handleAddMobileNotification}
                            >
                              Add Mobile Number
                            </Button>
                            <br />
                            <Box id="mobileN" className={styles.paddingTop}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                }}
                                key={99999}
                              >
                                <Box sx={{ flexGrow: 8, flexBasis: "10%" }}></Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexGrow: 1,
                                    alignContent: "center",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Typography>Remove</Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexGrow: 1,
                                    alignContent: "center",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Typography>Active</Typography>
                                </Box>
                              </Box>
                              {mobileNotifications.map((mobileNotification, index) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                  }}
                                  key={index}
                                  data-testid="notif-mobile-input"
                                >
                                  <Box sx={{ flexGrow: 8, flexBasis: "10%" }}>
                                    <MobileNumberField
                                      key={"mobile-" + index}
                                      id={"mobile-" + index}
                                      error={touched.mobile && Boolean(errors.mobile)}
                                      helperText={touched.mobile && errors.mobile}
                                      inputChange={(value: any) => {
                                        handleMobileNotificationText(index, value);
                                      }}
                                      initState={mobileNotification.mobile}
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexGrow: 1,
                                      alignContent: "center",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <IconButton
                                      color="secondary"
                                      aria-label="Delete Mobile Number"
                                      component="span"
                                      size="small"
                                      onClick={handleDeleteMobileNotification(index)}
                                    >
                                      <HighlightOffIcon />
                                    </IconButton>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexGrow: 1,
                                      alignContent: "center",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Checkbox
                                      key={"enabled-" + index}
                                      color="primary"
                                      checked={mobileNotification.enabled ? mobileNotification.enabled : false}
                                      onChange={handleMobileNotificationEnabled(index)}
                                    />
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </>
                        )}
                      </Box>
                      <Box style={{ marginTop: "20px" }}>
                        <hr className={styles.dashed} />
                      </Box>
                      <Box>
                        <Typography color="primary" className={styles.subHeading}>
                          SMS Delivery Options
                        </Typography>
                        <Box mt={2} display="flex" alignItems="center">
                          <Checkbox
                            checked={onlyBusinessHours}
                            name="onlyBusinessHours"
                            color="primary"
                            onChange={(_, checked) => {
                              setOnlyBusinessHours(checked);
                            }}
                          />
                          <Typography>Only send during business hours (8:00am - 6:00pm)</Typography>
                        </Box>
                        <Box mt={2} display="flex" alignItems="center" style={{ margin: "0 30px" }}>
                          <Checkbox
                            checked={includeWeekends}
                            name="includeWeekends"
                            color="primary"
                            disabled={!onlyBusinessHours}
                            onChange={(_, checked) => {
                              setIncludeWeekends(checked);
                            }}
                          />
                          <Typography>Include weekends</Typography>
                        </Box>
                        <Box style={{ marginTop: "20px" }}>
                          <hr />
                        </Box>
                      </Box>
                      <Box>
                        <Typography color="primary" className={styles.subHeading}>
                          Web Push Notifications
                        </Typography>
                        <Box mt={2} display="flex" alignItems="center">
                          <Typography>
                            You have {devices.filter((device) => device.platform === "web").length} web browser(s)
                            enabled to receive notifications
                          </Typography>
                        </Box>
                        <Box mt={2} display="flex" alignItems="center">
                          <Typography>
                            To receive notifications, verify that notifications for eResults are enabled in your
                            browser(s) settings <SettingsIcon fontSize="inherit" />
                          </Typography>
                        </Box>
                        <Box style={{ marginTop: "20px" }}>
                          <hr />
                        </Box>
                      </Box>
                      {mobility && (
                        <Box>
                          <Typography color="primary" className={styles.subHeading}>
                            Push Notifications
                          </Typography>
                          <Box mt={2} display="flex" alignItems="center">
                            <Typography>
                              You have {devices.filter((device) => device.platform !== "web").length} connected
                              device(s)
                            </Typography>
                          </Box>
                          <Box mt={2} display="flex" alignItems="center">
                            <Typography>
                              To receive notifications, notifications for the eResults App must be enabled on your
                              device(s). Please check your device Settings <SettingsIcon fontSize="inherit" />
                            </Typography>
                          </Box>
                          <Box style={{ marginTop: "20px" }}>
                            <hr />
                          </Box>
                        </Box>
                      )}
                    </span>
                    <Box mt={2} display="block" justifyContent="space-between" alignItems="center">
                      <Typography color="primary" className={styles.subHeading}>
                        Please confirm your notification settings:
                      </Typography>
                      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                        <Checkbox
                          checked={confirmNotification}
                          name="previewMode"
                          color="primary"
                          onChange={(_, checked) => {
                            setConfirmNotification(checked);
                          }}
                        />
                        <Typography>
                          The Notifications feature should not be a complete replacement for checking eResults for
                          results, as there may be rare circumstances which prevent you from receiving your
                          notifications. Please add noreply@clinicallabs.com.au to your safe senders list.
                        </Typography>
                      </Box>
                    </Box>
                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">{errorMessage && <Typography color="error" role="alert">{errorMessage}</Typography>}</Box>
                    <Box mt={2} display="flex" justifyContent="flex-end" alignItems="center">
                      {submitSuccess && <Box className={styles.successMessage}>Successfully saved your changes.</Box>}
                      <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={
                          loading ||
                          configLoading ||
                          emailConfigLoading ||
                          usersEmailMobileLoading ||
                          !confirmNotification
                        }
                      >
                        Save
                      </Button>
                    </Box>
                  </form>
                );
              }}
            </Formik>
          </Box>
        </AuthLayout>
      </Grid>
    </Grid>
  );
};

const NotificationSettingsPage: React.FC<any> = () => {
  const [success, setSuccess] = useState(false);
  const [usersEmailConfig, { loading, error }] = useMutation<UsersEmailConfig, UsersEmailConfigVariables>(
    UsersEmailConfigMutation,
  );

  if (error) {
    console.error("Error", error);
  }

  const onSubmit =
    (usersEmailConfigFunc: useMutation.ResultTuple<UsersEmailConfig, UsersEmailConfigVariables>[0]) =>
    async (
      notificationConfig: NotificationType[],
      notificationEmails: userEmail[],
      notificationMobiles: userMobile[],
      smsDelivery: smsDelivery,
    ) => {
      try {
        setSuccess(false);

        const varNotificationConfig: userConfig[] = [];

        for (const cfg of notificationConfig) {
          let duration = 0;

          for (const time of cfg.timeframe) {
            if (time.checked) {
              duration = time.minute;
              break;
            }
          }

          varNotificationConfig.push({
            userid: parseInt(authService.getUserId()),
            notificationTypeId: cfg.id,
            duration: duration,
            pendingEmail: cfg.pendingEmail,
          });
        }

        await usersEmailConfigFunc({
          variables: {
            input: {
              userConfigs: [...varNotificationConfig],
              userEmails: [...notificationEmails],
              userMobiles: [...notificationMobiles],
              smsDelivery: smsDelivery,
            },
          },
        });

        if (!error) {
          setSuccess(true);
        }
      } catch {
        setTimeout(function () {
          setSuccess(false);
        }, 3000);
      }
    };

  return (
    <Fragment>
      <StandardizedHeader title={"Notification Settings"} />
      <NotificationEmailBox
        onSubmit={onSubmit(usersEmailConfig)}
        error={error}
        loading={loading}
        submitSuccess={success}
      />
    </Fragment>
  );
};

export default NotificationSettingsPage;
